import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api, { formatoMXN, formatoFecha, ESTADOS, PRIORIDADES } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

export default function Ordenes() {
  const { usuario } = useAuth();
  const esMecanico = usuario?.rol === 'mecanico';
  const [ordenes, setOrdenes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/ordenes', {
        params: {
          busqueda,
          estado: filtroEstado === 'all' ? '' : filtroEstado,
          prioridad: filtroPrioridad === 'all' ? '' : filtroPrioridad
        }
      });
      setOrdenes(res.data);
    } catch { toast.error('Error al cargar ordenes'); }
    finally { setCargando(false); }
  }, [busqueda, filtroEstado, filtroPrioridad]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ordenes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">
          {esMecanico ? 'Mis Órdenes' : 'Órdenes de Trabajo'}
        </h1>
        {!esMecanico && (
        <Button onClick={() => navigate('/ordenes/nueva')} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nueva-orden-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Orden
        </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Buscar órdenes..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600" data-testid="buscar-ordenes-input" />
        </div>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="w-full sm:w-[180px] bg-slate-900 border-slate-800 text-slate-200" data-testid="filtro-estado-select">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-slate-300">Todos los estados</SelectItem>
            {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-300">{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroPrioridad} onValueChange={setFiltroPrioridad}>
          <SelectTrigger className="w-full sm:w-[180px] bg-slate-900 border-slate-800 text-slate-200" data-testid="filtro-prioridad-select">
            <SelectValue placeholder="Todas las prioridades" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-slate-300">Todas las prioridades</SelectItem>
            {Object.entries(PRIORIDADES).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-300">{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {cargando ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 bg-slate-800" />)}</div>
      ) : ordenes.length === 0 ? (
        <div className="text-center py-16" data-testid="ordenes-empty">
          <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No hay ordenes de trabajo</p>
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Orden</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Vehiculo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden sm:table-cell">Prioridad</TableHead>
                {!esMecanico && <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total</TableHead>}
                {!esMecanico && <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right hidden md:table-cell">Saldo</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordenes.map(o => (
                <TableRow key={o.id} className="border-slate-800/50 hover:bg-slate-800/40 cursor-pointer"
                  onClick={() => navigate(`/ordenes/${o.id}`)} data-testid={`orden-row-${o.id}`}>
                  <TableCell className="font-mono text-xs text-amber-500 font-semibold">{o.numero_orden}</TableCell>
                  <TableCell className="text-sm text-slate-300">{o.cliente_nombre}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden md:table-cell max-w-[150px] truncate">{o.vehiculo_info || '-'}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${ESTADOS[o.estado]?.clase}`}>
                      {ESTADOS[o.estado]?.label || o.estado}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${PRIORIDADES[o.prioridad]?.clase}`}>
                      {PRIORIDADES[o.prioridad]?.label || o.prioridad}
                    </span>
                  </TableCell>
                  {!esMecanico && <TableCell className="text-sm text-slate-300 text-right font-mono">{formatoMXN(o.total)}</TableCell>}
                  {!esMecanico && <TableCell className="text-sm text-right hidden md:table-cell">
                    <span className={o.saldo_pendiente > 0 ? 'text-orange-400 font-mono' : 'text-green-400 font-mono'}>
                      {formatoMXN(o.saldo_pendiente)}
                    </span>
                  </TableCell>}
                  <TableCell className="text-xs text-slate-500 hidden lg:table-cell">{formatoFecha(o.fecha_ingreso || o.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
